import { createContext, useContext } from 'react';

import { type ExtractedTableJson } from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import { useLiveTableResults } from 'utils/hooks/taskTracking/useLiveTableResults';

export type ExtractedTableContextType = {
  extractedTables: ExtractedTableJson[];
  isTablesDataFetching: boolean;
};

export const DEFAULT_CONTEXT: ExtractedTableContextType = {
  extractedTables: [],
  isTablesDataFetching: false,
};

export const ExtractedTableContext =
  createContext<ExtractedTableContextType>(DEFAULT_CONTEXT);

type ExtractedTableProviderProps = {
  children: React.ReactNode;
  partFileUuids: string[];
};

/**
 * Handles fetching all extracted tables for the given part file uuids
 */
export const ExtractedTableProvider = ({
  children,
  partFileUuids,
}: ExtractedTableProviderProps) => {
  const memoizedPartFileUuids = useMemoizedArray(partFileUuids);

  const { isTablesDataFetching, extractedTablesData } = useLiveTableResults({
    partFileUuids: memoizedPartFileUuids,
  });

  return (
    <ExtractedTableContext.Provider
      value={{
        extractedTables: extractedTablesData ?? [],
        isTablesDataFetching,
      }}
    >
      {children}
    </ExtractedTableContext.Provider>
  );
};

export const useExtractedTables = () => {
  return useContext<ExtractedTableContextType>(ExtractedTableContext);
};
